import { create } from 'zustand';
import { DEFAULT_ASSETS, resolveAssets } from './assets';

const lsGet = (k, d) => { try { return localStorage.getItem(k) ?? d; } catch { return d; } };
const lsSet = (k, v) => { try { localStorage.setItem(k, v); } catch { /* cuota */ } };

/**
 * Estado de la capa LMX. Nada acá toca el canvas: los cambios del proyecto
 * los hace el agente con sus herramientas, en el servidor.
 */
export const useLmxStore = create((set, get) => ({
  // ── acceso ──
  ready: false,
  available: false,
  prefs: null,
  assets: DEFAULT_ASSETS,
  limits: null,
  model: null,

  // ── capa ──
  mode: 'curtain',           // curtain (telón + burbuja grande) | open | docked
  greeted: false,
  busy: false,
  notify: false,
  pose: 'idle',
  status: { text: 'En espera', active: false, warn: false },
  // Apagado por defecto: el telón difuminaba el canvas de entrada.
  veil: lsGet('lmx-veil', '0') === '1',
  showLog: false,
  building: false,

  // ── conversación ──
  turn: { text: '', cards: [] },   // lo que se ve en el globo (último turno)
  log: [],                         // historial completo, visible con "Ver conversación"
  pendingConfirm: null,
  conversationId: null,
  // Envío LMX-3 · imágenes para el próximo pedido:
  // [{key, name, preview, status: uploading|ready|error, assetId, url}]
  attachments: [],
  // Envío LMX-T · trabajos del proyecto (lo que devuelve GET /lmx/jobs).
  jobs: null,
  runs: [],

  setJobs: (jobs) => set({ jobs }),
  setRuns: (runs) => set({ runs: runs || [] }),

  setAvailability: (data) => set({
    ready: true,
    available: !!data?.enabled,
    prefs: data?.prefs || null,
    assets: resolveAssets(data?.assets),
    limits: data?.limits || null,
    model: data?.model || null,
  }),
  setUnavailable: () => set({ ready: true, available: false }),

  setPrefs: (patch) => set((s) => ({ prefs: { ...(s.prefs || {}), ...patch } })),

  open: () => set({ mode: 'open', notify: false }),
  // Al cerrar, la burbuja queda chica abajo y el telón se va: la plataforma
  // queda usable. El telón opaco es sólo la entrada.
  collapse: () => set({ mode: 'docked' }),
  toggle: () => (get().mode === 'open' ? get().collapse() : get().open()),
  markGreeted: () => set({ greeted: true }),
  setVeil: (veil) => { lsSet('lmx-veil', veil ? '1' : '0'); set({ veil }); },
  toggleLog: () => set((s) => ({ showLog: !s.showLog })),
  setBuilding: (building) => set({ building }),

  setPose: (pose) => set({ pose }),
  setStatus: (text, active = false, warn = false) => set({ status: { text, active, warn } }),
  setBusy: (busy) => set({ busy }),
  notifyOrb: () => set((s) => (s.mode === 'open' ? {} : { notify: true })),

  setConversation: (conversationId) => set({ conversationId }),

  addAttachment: (att) => set((s) => ({ attachments: [...s.attachments, att] })),
  updateAttachment: (key, patch) => set((s) => ({
    attachments: s.attachments.map((a) => (a.key === key ? { ...a, ...patch } : a)),
  })),
  removeAttachment: (key) => set((s) => ({
    attachments: s.attachments.filter((a) => a.key !== key),
  })),
  clearAttachments: () => set({ attachments: [] }),

  /** Arranca un turno: el pedido del usuario entra al historial. */
  pushUser: (text, images = []) => set((s) => ({
    log: [...s.log, { who: 'Vos', text, images }],
    turn: { text: '', cards: [] },
    pendingConfirm: null,
  })),

  appendDelta: (text) => set((s) => {
    // Dos bloques del modelo seguidos (o la respuesta después de Aprobar)
    // llegan pegados: "después.Listo" → se separan en párrafos.
    const prev = s.turn.text;
    const glue = prev && /[.!?:)…]$/.test(prev) && /^[A-ZÁÉÍÓÚÑ¿¡]/.test(text) ? '\n\n' : '';
    return { turn: { ...s.turn, text: prev + glue + text } };
  }),

  pushCard: (card) => set((s) => ({
    turn: { ...s.turn, cards: [...s.turn.cards, card] },
  })),

  /** Fila de progreso: tool_start crea la fila, tool_end la resuelve. */
  trackTool: (id, label, state, meta, tool) => set((s) => {
    const cards = [...s.turn.cards];
    let idx = -1;
    for (let i = cards.length - 1; i >= 0; i -= 1) {
      if (cards[i].type === 'progress') { idx = i; break; }
    }
    if (idx < 0) { cards.push({ type: 'progress', items: [] }); idx = cards.length - 1; }
    const items = [...(cards[idx].items || [])];
    const at = items.findIndex((i) => i.id === id);
    const row = {
      id, label: label || items[at]?.label || 'trabajando', state, meta,
      tool: tool || items[at]?.tool || null,
    };
    if (at >= 0) items[at] = { ...items[at], ...row };
    else items.push(row);
    cards[idx] = { ...cards[idx], items };
    return { turn: { ...s.turn, cards } };
  }),

  /** Cierra el turno: el texto del agente queda en el historial. */
  closeTurn: () => set((s) => (s.turn.text.trim()
    ? { log: [...s.log, { who: 'Lumière', text: s.turn.text.trim() }] }
    : {})),

  setPendingConfirm: (pendingConfirm) => set({ pendingConfirm }),

  /** Rehidratación al recargar: último turno al globo, el resto al historial.
      Si quedó una confirmación pendiente, vuelve con Aprobar/Cancelar. */
  hydrate: ({ conversation_id: conversationId, messages, pending }) => {
    const rows = messages || [];
    if (!rows.length && !pending) return;
    const log = rows
      .filter((m) => (m.text || '').trim())
      .map((m) => ({
        who: m.role === 'user' ? 'Vos' : 'Lumière',
        text: m.text,
        // Los adjuntos del usuario se guardan como tarjeta results.
        images: m.role === 'user'
          ? (m.cards || []).flatMap((c) => (c.type === 'results' ? (c.items || []).map((i) => i.url) : []))
          : [],
      }));
    let last = null;
    for (let i = rows.length - 1; i >= 0; i -= 1) {
      if (rows[i].role === 'assistant') { last = rows[i]; break; }
    }
    set({
      conversationId,
      log,
      greeted: true,
      turn: last ? { text: last.text || '', cards: last.cards || [] } : { text: '', cards: [] },
      pendingConfirm: pending || null,
      ...(pending ? {
        pose: 'point',
        status: { text: 'Esperando tu aprobación', active: true, warn: false },
      } : {}),
    });
  },

  reset: () => set({
    turn: { text: '', cards: [] }, log: [], pendingConfirm: null,
    pose: 'idle', status: { text: 'En espera', active: false, warn: false },
  }),
}));

export default useLmxStore;
